import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faBriefcase } from "@fortawesome/pro-regular-svg-icons/faBriefcase";
import { faCube } from "@fortawesome/pro-regular-svg-icons/faCube";
import { faHouseBuilding } from "@fortawesome/pro-regular-svg-icons/faHouseBuilding";
import { faAddressCard } from "@fortawesome/pro-regular-svg-icons/faAddressCard";
import { faFolderOpen } from "@fortawesome/pro-regular-svg-icons/faFolderOpen";
import { faLocationDot } from "@fortawesome/pro-regular-svg-icons/faLocationDot";
import { faUser } from "@fortawesome/pro-regular-svg-icons/faUser";
import { faEnvelope } from "@fortawesome/pro-regular-svg-icons/faEnvelope";
import { faPhone } from "@fortawesome/pro-regular-svg-icons/faPhone";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import { cn } from "../lib/utils";

interface Props {
  element: DocumentElementType;
}

type EntityType =
  | "job"
  | "managed-property"
  | "asset"
  | "contact"
  | "project";

interface EntityRecord {
  id: string;
  domainId: number;
  name: string;
  refId?: string | null;
  status?: string | null;
  propertyName?: string | null;
  customerName?: string | null;
  addressFormatted?: string | null;
  email?: string | null;
  mobile?: string | null;
  businessName?: string | null;
}

// Canonical entity styling (mirrors @eaco/design-tokens ENTITY_DEFS + the web
// EntityReference), inlined so this read-only card carries no app dependency.
const STYLE: Record<
  EntityType,
  { glyph: IconProp; color: string; label: string; circle?: boolean }
> = {
  job: { glyph: faBriefcase, color: "#2563eb", label: "JOB" },
  "managed-property": {
    glyph: faHouseBuilding,
    color: "#16a34a",
    label: "MANAGED PROPERTY",
  },
  asset: { glyph: faCube, color: "#059669", label: "ASSET" },
  contact: {
    glyph: faAddressCard,
    color: "#ea580c",
    label: "CONTACT",
    circle: true,
  },
  project: { glyph: faFolderOpen, color: "#4f46e5", label: "PROJECT" },
};

const JOB_PILL = { color: "#2563c9", bg: "#e6effc" };
const ASSET_PILL = { color: "#a85f08", bg: "#fbf0e6" };

const initialsOf = (name: string): string =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

interface Fact {
  icon: IconProp;
  text: string;
}
const fact = (icon: IconProp, text?: string | null): Fact | null =>
  text ? { icon, text } : null;

interface CardModel {
  title: string;
  entityId?: string;
  status?: { text: string; color: string; bg: string };
  meta: Fact[];
}

function cardModel(type: EntityType, r: EntityRecord): CardModel {
  const clean = (facts: (Fact | null)[]): Fact[] =>
    facts.filter(Boolean) as Fact[];
  switch (type) {
    case "job":
      return {
        title: r.name,
        entityId: r.refId || undefined,
        status: r.status ? { text: r.status.toUpperCase(), ...JOB_PILL } : undefined,
        meta: clean([fact(faLocationDot, r.propertyName), fact(faUser, r.customerName)]),
      };
    case "managed-property":
      return {
        title: r.name,
        entityId: r.refId || undefined,
        meta: clean([
          fact(faLocationDot, r.addressFormatted),
          fact(faUser, r.customerName),
        ]),
      };
    case "asset":
      return {
        title: r.name,
        entityId: r.refId || undefined,
        status: r.status
          ? { text: r.status.toUpperCase(), ...ASSET_PILL }
          : undefined,
        meta: clean([fact(faLocationDot, r.propertyName), fact(faUser, r.customerName)]),
      };
    case "contact":
      return {
        title: r.businessName ? `${r.name} · ${r.businessName}` : r.name,
        meta: clean([fact(faEnvelope, r.email), fact(faPhone, r.mobile)]),
      };
    case "project":
      return { title: r.name, meta: clean([fact(faUser, r.customerName)]) };
  }
}

/**
 * Read-only render of a page-document entity reference — a card for a linked
 * Eaco record (job/property/asset/contact/project), built from the block's
 * stored snapshot (`data.pick`). The editor's live, navigating version lives in
 * apps/web; this static card is what a PDF / portal shows.
 */
const PageEntityView: React.FC<Props> = ({ element }) => {
  const pick = (element.data as { pick?: { entityType?: EntityType; record?: EntityRecord } })
    ?.pick;
  const type = pick?.entityType;
  const record = pick?.record;
  if (!type || !record || !STYLE[type]) return null;

  const s = STYLE[type];
  const wash = `${s.color}1f`;
  const m = cardModel(type, record);

  return (
    <div className="flex items-center gap-[14px] rounded-[11px] border border-[#e7e5e1] bg-white px-[16px] py-[14px] font-jakarta">
      <span
        aria-hidden
        className={cn(
          "flex h-[40px] w-[40px] flex-none items-center justify-center",
          s.circle ? "rounded-full" : "rounded-[10px]",
        )}
        style={{ background: wash, color: s.color }}
      >
        {s.circle ? (
          <span className="text-[14px] font-extrabold">
            {initialsOf(record.name)}
          </span>
        ) : (
          <FontAwesomeIcon icon={s.glyph} className="text-[20px]" />
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-[8px]">
          <span
            className={cn(
              "text-[11px] font-extrabold tracking-[0.03em] text-[#8f8b83]",
              m.entityId && "font-jbmono",
            )}
          >
            {m.entityId ?? s.label}
          </span>
          {m.status && (
            <span
              className="rounded-[20px] px-[9px] py-[2px] text-[10.5px] font-extrabold tracking-[0.02em]"
              style={{ color: m.status.color, background: m.status.bg }}
            >
              {m.status.text}
            </span>
          )}
        </span>
        <span className="mt-[3px] block truncate text-[14.5px] font-bold text-[#2a2723]">
          {m.title}
        </span>
        {m.meta.length > 0 && (
          <span className="mt-[5px] flex items-center gap-[12px] text-[12px] text-[#8f8b83]">
            {m.meta.slice(0, 2).map((f, i) => (
              <span key={i} className="inline-flex min-w-0 items-center gap-[5px]">
                <FontAwesomeIcon icon={f.icon} className="text-[12px]" />
                <span className="truncate">{f.text}</span>
              </span>
            ))}
          </span>
        )}
      </span>

      <FontAwesomeIcon
        icon={faChevronRight}
        className="flex-none text-[17px] text-[#b4b0a8]"
      />
    </div>
  );
};

export default PageEntityView;
